/** Section 2 — tonight's title and message (shown on the fan screens) plus the
 *  default goal window. Default text is neutral: no team, league or logo. */
import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { CopyField, autoTranslate, builtIn, isAuto, mergeCopy } from './CopyFields';
import api from '../../../lib/api';
import { btn } from './buttons';

const KEYS = ['tonightTitle', 'tonightBody'];

const pick = (copy) => ({
    en: Object.fromEntries(KEYS.map((k) => [k, (copy?.en || {})[k] || ''])),
    fr: Object.fromEntries(KEYS.map((k) => [k, (copy?.fr || {})[k] || ''])),
});

export const GoalTonightBlock = ({ cfg, onSave }) => {
    const [draft, setDraft] = useState(() => pick(cfg.copy));
    const [window_, setWindow] = useState(cfg.window_seconds);
    const [busy, setBusy] = useState(false);
    // Which French boxes the machine has just filled, and which it could not.
    const [auto, setAuto] = useState(cfg.copy_auto_fr || {});
    const [failed, setFailed] = useState([]);

    useEffect(() => {
        setDraft(pick(cfg.copy));
        setWindow(cfg.window_seconds);
        setAuto(cfg.copy_auto_fr || {});
    }, [cfg]);

    const change = (lang, field, value) =>
        setDraft((p) => ({ ...p, [lang]: { ...p[lang], [field]: value } }));

    // Only the ENGLISH fields this save actually changed.
    const changedEn = (next) => KEYS.filter(
        (k) => (next.en[k] || '') !== ((cfg.copy?.en || {})[k] || ''));

    const applyTranslation = (out) => {
        if (!out) return;
        setFailed(out.failed || []);
        if (!(out.translated || []).length) return;
        setAuto(out.copy_auto_fr || {});
        setDraft((p) => ({ ...p, fr: { ...p.fr, ...pick(out.copy).fr } }));
        toast.success('French filled in automatically');
    };

    const save = async () => {
        setBusy(true);
        const keys = changedEn(draft);
        try {
            await onSave({
                copy: mergeCopy(cfg.copy, draft),
                window_seconds: Math.max(cfg.window_min, Math.min(cfg.window_max, Number(window_) || cfg.window_min)),
            });
        } finally { setBusy(false); }
        // After the save, never during it.
        applyTranslation(await autoTranslate(api, cfg, keys));
    };

    const retranslate = async (field) => {
        if (!window.confirm('Replace the French text of this field with a fresh translation of the English?')) return;
        applyTranslation(await autoTranslate(api, cfg, [field], true));
    };

    const frProps = (key) => ({
        auto: isAuto({ copy_auto_fr: auto }, key, draft.fr[key]),
        failed: failed.includes(key),
        onRetranslate: retranslate,
    });

    return (
        <div className="space-y-4 pt-3">
            {/* ODDS SAID WIN, NO PRIZE TO GIVE. Shows only when it has actually
                happened on this promotion. Nothing else here changes. */}
            {cfg.no_prize_alert?.reason && (
                <p className="rounded-lg border border-amber-400 bg-amber-50 px-3 py-2 text-sm
                              text-amber-900 dark:bg-amber-400/15 dark:text-amber-200"
                   data-testid="gg-no-prize-alert">
                    <strong>Odds say win but no prize is available:</strong>{' '}
                    {cfg.no_prize_alert.reason}
                    {cfg.no_prize_alert.at ? ` · last seen ${new Date(cfg.no_prize_alert.at).toLocaleString()}` : ''}
                </p>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
                <CopyField label="Tonight's game — title" lang="en" field="tonightTitle"
                           value={draft.en.tonightTitle} onChange={change}
                           placeholder={builtIn('en', 'tonightTitle')} testid="gg-tonight-title-en" />
                <CopyField label="Tonight's game — title" lang="fr" field="tonightTitle"
                           value={draft.fr.tonightTitle} onChange={change}
                           placeholder={builtIn('fr', 'tonightTitle')} testid="gg-tonight-title-fr"
                           {...frProps('tonightTitle')} />
                <CopyField label="Tonight's game — message" lang="en" field="tonightBody" textarea
                           value={draft.en.tonightBody} onChange={change}
                           placeholder={builtIn('en', 'tonightBody')} testid="gg-tonight-body-en" />
                <CopyField label="Tonight's game — message" lang="fr" field="tonightBody" textarea
                           value={draft.fr.tonightBody} onChange={change}
                           placeholder={builtIn('fr', 'tonightBody')} testid="gg-tonight-body-fr"
                           {...frProps('tonightBody')} />
            </div>
            <label className="block max-w-xs">
                <span className="block text-xs font-medium text-slate-500 mb-1">
                    Default goal window ({cfg.window_min}–{cfg.window_max} seconds)
                </span>
                <input
                    type="number" min={cfg.window_min} max={cfg.window_max}
                    value={window_} onChange={(e) => setWindow(e.target.value)}
                    data-testid="gg-window-seconds"
                    className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm tabular-nums"
                />
            </label>
            <button type="button" onClick={save} disabled={busy} data-testid="gg-tonight-save"
                    className={btn.primary}>
                {busy ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />}
                Save tonight's game
            </button>
        </div>
    );
};

export default GoalTonightBlock;
